import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, Save } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { host: "", port: 587, security: "starttls", username: "", password: "",
  from_address: "", from_name: "", reply_to: "", timeout_seconds: 20 };

export function SmtpSettings() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState("");
  const [failed, setFailed] = useState(false);

  const load = () => api.get("/admin/email/config").then((r) => {
    setConfig(r.data);
    setForm((f) => ({ ...f,
      host: r.data.host || "", port: r.data.port || 587, security: r.data.security || "starttls",
      username: r.data.username || "", password: "", from_address: r.data.from_address || "",
      from_name: r.data.from_name || "", reply_to: r.data.reply_to || "",
    }));
  }).catch((e) => { setFailed(true); setMessage(formatApiError(e.response?.data?.detail)); })
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true); setMessage(""); setFailed(false);
    try {
      const payload = { ...form, port: parseInt(form.port, 10), timeout_seconds: parseInt(form.timeout_seconds, 10) || 20 };
      if (!payload.password) delete payload.password; // leeg = ongewijzigd laten
      const { data } = await api.put("/admin/email/config", payload);
      setConfig(data);
      setForm((f) => ({ ...f, password: "" }));
      toast.success("Mailserver opgeslagen");
    } catch (e) { setFailed(true); setMessage(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setMessage(""); setFailed(false);
    try { await api.post("/admin/email/test"); setFailed(false);
      setMessage("De SMTP-server heeft de testmail geaccepteerd. Controleer je inbox en spammap."); }
    catch (e) { setFailed(true); setMessage(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  if (loading) return <Card className="p-6" data-testid="smtp-settings"><Loader2 className="w-6 h-6 animate-spin text-primary" /></Card>;

  return (
    <Card className="p-6 space-y-5" data-testid="smtp-settings">
      <div>
        <h3 className="font-heading font-semibold flex items-center gap-2"><Mail className="w-4 h-4" /> E-mail via eigen SMTP</h3>
        <p className={`text-sm mt-1 ${config?.configured ? "text-red-700" : "text-amber-800"}`} data-testid="smtp-status">
          {config?.configured
            ? `SMTP is ingesteld (${config.source === "database" ? "in de app" : "via server-omgeving"}).`
            : "SMTP is nog niet ingesteld. Uitnodigings- en meldingsmails worden niet verstuurd; in-app en push blijven werken."}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label>Server (host)</Label>
          <Input value={form.host} onChange={set("host")} placeholder="smtp.jouwprovider.nl" data-testid="smtp-host" /></div>
        <div className="space-y-1.5"><Label>Poort</Label>
          <Input type="number" value={form.port} onChange={set("port")} placeholder="587" data-testid="smtp-port" /></div>
        <div className="space-y-1.5"><Label>Beveiliging</Label>
          <select value={form.security} onChange={set("security")} data-testid="smtp-security"
            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
            <option value="starttls">STARTTLS (meestal poort 587)</option>
            <option value="ssl">SSL/TLS (meestal poort 465)</option>
          </select></div>
        <div className="space-y-1.5"><Label>Time-out (seconden)</Label>
          <Input type="number" value={form.timeout_seconds} onChange={set("timeout_seconds")} data-testid="smtp-timeout" /></div>
        <div className="space-y-1.5"><Label>Gebruikersnaam</Label>
          <Input value={form.username} onChange={set("username")} placeholder="bijv. je e-mailadres" data-testid="smtp-username" /></div>
        <div className="space-y-1.5"><Label>Wachtwoord</Label>
          <Input type="password" value={form.password} onChange={set("password")}
            placeholder={config?.password_set ? "•••••••• (laat leeg = ongewijzigd)" : "wachtwoord"} data-testid="smtp-password" /></div>
        <div className="space-y-1.5"><Label>Afzender e-mail</Label>
          <Input value={form.from_address} onChange={set("from_address")} placeholder="planning@jouwclub.nl" data-testid="smtp-from-address" /></div>
        <div className="space-y-1.5"><Label>Afzendernaam</Label>
          <Input value={form.from_name} onChange={set("from_name")} placeholder="Bar 't Kelderke" data-testid="smtp-from-name" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Antwoord-naar (optioneel)</Label>
          <Input value={form.reply_to} onChange={set("reply_to")} placeholder="optioneel reply-to adres" data-testid="smtp-reply-to" /></div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={save} disabled={saving} data-testid="smtp-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Mailserver opslaan
        </Button>
        <Button variant="outline" onClick={test} disabled={testing || !config?.configured} data-testid="smtp-test-button">
          {testing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Testmail naar mijn e-mailadres
        </Button>
      </div>
      {config?.configured && (
        <p className="text-xs text-muted-foreground" data-testid="smtp-config-summary">
          Actief: {config.host}:{config.port} · {config.security} · {config.from_address}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        STARTTLS gebruikt meestal poort 587, SSL/TLS poort 465. Het wachtwoord wordt veilig op je eigen server bewaard en nooit teruggestuurd naar de browser.
      </p>
      {message && <p role={failed ? "alert" : "status"} className={`text-sm ${failed ? "text-rose-700" : "text-red-700"}`} data-testid="smtp-test-result">{message}</p>}
    </Card>
  );
}
